import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import React from "react";
import ReactDOMServer from "react-dom/server";
import { formatCurrencyValueCompact } from "~/utils/functions/formatCurrencyValueCompact";
import {
    PieChartSeries,
    PieChartTooltipOptions,
    PiePlotOptions,
} from "./types";
import {
    DEFAULT_PIE_CHART_TOOLTIP_PROPS,
    DEFAULT_PIE_PLOT_OPTIONS,
} from "./consts";

// // // //

const SLICE_BORDER_COLOR = "var(--border)";
const SLICE_TEXT_STROKE_COLOR = "var(--border)";

function getPiechartOptions(props: {
    series: PieChartSeries[];
    plotOptions: PiePlotOptions;
    tooltip: PieChartTooltipOptions;
    height?: number;
    innerSize?: number | string;
    onClick?: (clikedItem: PieChartSeries) => void;
    animation?: boolean;
    totalDisplay?: React.ReactNode;
}) {
    const {
        onClick,
        plotOptions,
        tooltip,
        height = 330,
        innerSize = 0,
        animation = true,
        totalDisplay,
    } = props;

    const totalDisplayHtml =
        totalDisplay !== undefined && totalDisplay !== null
            ? ReactDOMServer.renderToString(<>{totalDisplay}</>)
            : null;

    // Sort the series from largest to smallest
    // NOTE - we deliberately make a copy of props.series because
    // invoking `.sort()` directly on `props.series` will update the prop value,
    // thus triggering a redundant re-render of the component
    const seriesProp = [...props.series];
    const series = seriesProp.sort((a, b) => (a.value > b.value ? -1 : 1));

    // Deconstruct tooltip props
    const {
        textColor: tooltipTextColor,
        style: tooltipStyle,
        outside: tooltipOutside,
        ...tooltipProps
    } = tooltip;

    const opts = {
        title: null,
        chart: {
            plotShadow: false,
            type: "pie",
            height,
            backgroundColor: "transparent",
            custom: {} as { label?: any },
            events: totalDisplayHtml
                ? {
                      render() {
                          const chart = this as any;
                          const series = chart.series[0];

                          // Destroy any prior label so the new one re-measures
                          // against the current text and gets re-positioned
                          // against the current series.center on every render.
                          if (chart.options.chart.custom.label) {
                              chart.options.chart.custom.label.destroy();
                              chart.options.chart.custom.label = undefined;
                          }

                          // useHTML=true so the React-rendered HTML is honored
                          const customLabel = chart.renderer
                              .label(
                                  totalDisplayHtml,
                                  undefined,
                                  undefined,
                                  undefined,
                                  undefined,
                                  undefined,
                                  true,
                              )
                              .add();
                          chart.options.chart.custom.label = customLabel;

                          // Center the HTML label on the pie's center.
                          // textAnchor doesn't apply to HTML, so we offset by
                          // half the rendered bounding box ourselves.
                          const bbox = customLabel.getBBox();
                          const x =
                              series.center[0] +
                              chart.plotLeft -
                              bbox.width / 2;
                          const y =
                              series.center[1] +
                              chart.plotTop -
                              bbox.height / 2;

                          customLabel.attr({ x, y });
                      },
                  }
                : undefined,
        },
        // tooltip,
        tooltip: {
            ...tooltipProps,
            // When a `totalDisplay` is rendered, mount the tooltip in a
            // separate <body>-level container so it stacks above the donut's
            // HTML label. `style.zIndex` doesn't help because the label and
            // tooltip live in sibling stacking contexts.
            outside: tooltipOutside ?? Boolean(totalDisplayHtml),
            style: {
                color: tooltipTextColor,
                ...tooltipStyle,
            },
        },
        plotOptions: {
            series: {
                animation: {
                    duration: 1000,
                },
            },
            pie: {
                dataLabels: {
                    ...plotOptions.dataLabels,
                    style: {
                        textOutline: SLICE_TEXT_STROKE_COLOR,
                    },
                },
                innerSize: innerSize,
                // showInLegend: true, // ENHANCEMENT - control this using props.legend
                cursor: onClick ? "pointer" : "default",
                events: {
                    click: (event: any) => {
                        const summary = event.point.summary;
                        if (summary && onClick) {
                            onClick(summary);
                        }
                    },
                },
                borderRadius: 2,
                borderColor: SLICE_BORDER_COLOR,
                animation,
            },
        },
        accessibility: {
            enabled: false,
        },
        credits: {
            enabled: false,
        },
        series: [
            {
                data: series.map((s) => {
                    // Build final series object passed into Highcharts
                    const series = {
                        name: s.name,
                        color: s.color,
                        y: s.value,
                        summary: s,
                        sliced: s.sliced,
                        dataLabelValue: formatCurrencyValueCompact(s.value),
                        tooltipValue: `${Math.round(s.value).toLocaleString()}`,
                    };

                    // Arbitrarily scale a value to make it take up more space in the chart
                    // NOTE - this is only here so we can coerce XDC to render in the charts
                    if (s.scale) {
                        series.y = s.value * s.scale;
                    }

                    // Return series object *with* custom `dataLabels` behavior
                    // NOTE - we have to apply s.dataLabels
                    // when it's available, because a value of `dataLabels: undefined`
                    // on the series object passed into Highcharts creates *all sorts of problems*
                    if (s.dataLabels) {
                        return {
                            ...series,
                            dataLabels: s.dataLabels,
                        };
                    }

                    // Return series object without custom `dataLabels` behavior
                    return series;
                }),
            },
        ],
        // ENHANCEMENT - add support for `props.legend`
        // legend: {
        //     enabled: true,
        //     align: "left",
        //     layout: "horizontal",
        //     verticalAlign: "bottom",
        //     itemWidth: 100,
        //     // itemStyle?: React.CSSProperties;
        // },
    };

    return opts;
}

// // // //

interface Props {
    series: PieChartSeries[];
    onClick?: (clikedItem: PieChartSeries) => void;
    plotOptions?: PiePlotOptions;
    tooltip?: PieChartTooltipOptions;
    height?: number;
    animation?: boolean;
    /**
     * When provided, the chart renders as a donut. Pass `{}` for default donut
     * styling, or supply `innerSize` / `totalDisplay` to customize.
     */
    donut?: {
        innerSize?: number | string;
        totalDisplay?: React.ReactNode;
    };
}

const DEFAULT_DONUT_INNER_SIZE = "60%";

/**
 * Generic multi-purpose PieChart visualization
 */
export function PieChart(props: Props) {
    const {
        series,
        onClick,
        height,
        plotOptions = DEFAULT_PIE_PLOT_OPTIONS,
        tooltip = DEFAULT_PIE_CHART_TOOLTIP_PROPS,
        animation = true,
        donut,
    } = props;

    // DEBUG - display empty message
    // if (series.length === 0) {
    //     return <p className="text-red-600 font-semibold text-xl">NO DATA</p>;
    // }

    const options = getPiechartOptions({
        series,
        onClick,
        height,
        plotOptions,
        tooltip,
        animation,
        innerSize: donut
            ? donut?.innerSize
                ? donut.innerSize
                : DEFAULT_DONUT_INNER_SIZE
            : undefined,
        totalDisplay: donut?.totalDisplay,
    });

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}
